/**
 * SortFilterBar — Excel-style sort + multi-select filter applied above any
 * saved-records list (card-based or tabular). Persists state to localStorage
 * keyed by the storageKey prop so a user's chosen sort/filter survives reloads
 * and visits across devices on the same browser.
 *
 * Usage:
 *   const columns = [
 *     { key: 'name',       label: 'Name',     accessor: r => r.name,                 type: 'text' },
 *     { key: 'species',    label: 'Species',  accessor: r => r.species,              type: 'text' },
 *     { key: 'updated_at', label: 'Updated',  accessor: r => r.updated_at,           type: 'date' },
 *     { key: 'dataPts',    label: 'Pts',      accessor: r => r.data_points?.length,  type: 'number' },
 *   ];
 *   const { processedRecords, bar } = useSortFilterBar(records, columns, 'spreadsheet-records');
 *   // render `bar` above your list, render `processedRecords` instead of `records`.
 */

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { ArrowUpDown, ArrowUp, ArrowDown, Filter, X, Save, Check, ChevronDown, Layers, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Checkbox } from './ui/checkbox';
import { Input } from './ui/input';

const STORAGE_PREFIX = 'pfd-sortfilter:';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const loadState = (storageKey) => {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + storageKey);
    if (!raw) return { sortKey: null, sortDir: null, filters: {} };
    const parsed = JSON.parse(raw);
    return {
      sortKey: parsed.sortKey ?? null,
      sortDir: parsed.sortDir ?? null,
      filters: parsed.filters ?? {},
    };
  } catch {
    return { sortKey: null, sortDir: null, filters: {} };
  }
};

const saveState = (storageKey, state) => {
  try {
    localStorage.setItem(STORAGE_PREFIX + storageKey, JSON.stringify(state));
  } catch {
    /* quota — ignore */
  }
};

const compareValues = (a, b, type) => {
  // Nullish always sorts last regardless of direction (Excel-parity behaviour).
  const aNil = a === null || a === undefined || a === '';
  const bNil = b === null || b === undefined || b === '';
  if (aNil && bNil) return 0;
  if (aNil) return 1;
  if (bNil) return -1;
  if (type === 'number') {
    return Number(a) - Number(b);
  }
  if (type === 'date') {
    const ta = new Date(a).getTime();
    const tb = new Date(b).getTime();
    if (Number.isNaN(ta) && Number.isNaN(tb)) return 0;
    if (Number.isNaN(ta)) return 1;
    if (Number.isNaN(tb)) return -1;
    return ta - tb;
  }
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
};

const formatForDisplay = (value, type) => {
  if (value === null || value === undefined || value === '') return '(blank)';
  if (type === 'date') {
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) return d.toLocaleDateString();
  }
  return String(value);
};

const FilterPopover = ({ column, records, filters, setFilter, clearFilter }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const selected = filters[column.key]; // either undefined (no filter) or Set-like array of allowed values

  // Build unique value list from records (using accessor before any filter).
  const uniqueValues = useMemo(() => {
    const seen = new Map(); // raw value → display label
    records.forEach((r) => {
      const v = column.accessor(r);
      const norm = v === null || v === undefined || v === '' ? '__BLANK__' : v;
      if (!seen.has(norm)) seen.set(norm, formatForDisplay(v, column.type));
    });
    return Array.from(seen.entries()).sort((a, b) =>
      compareValues(a[1], b[1], 'text'),
    );
  }, [records, column]);

  const filtered = useMemo(() => {
    if (!search) return uniqueValues;
    const s = search.toLowerCase();
    return uniqueValues.filter(([, label]) => label.toLowerCase().includes(s));
  }, [uniqueValues, search]);

  const isActive = Array.isArray(selected) && selected.length > 0;
  const allKeys = uniqueValues.map(([k]) => k);
  const allChecked = !isActive || (selected && selected.length === allKeys.length);

  const toggleValue = (key) => {
    const current = Array.isArray(selected) ? selected : allKeys;
    const next = current.includes(key)
      ? current.filter((k) => k !== key)
      : [...current, key];
    if (next.length === allKeys.length) {
      // back to "no filter"
      clearFilter(column.key);
    } else if (next.length === 0) {
      // empty → still treat as filter (show nothing) but flag it
      setFilter(column.key, next);
    } else {
      setFilter(column.key, next);
    }
  };

  const selectAll = () => clearFilter(column.key);
  const selectNone = () => setFilter(column.key, []);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Filter by ${column.label}`}
          title={`Filter by ${column.label}`}
          className={`inline-flex items-center justify-center w-5 h-5 rounded transition-colors ${
            isActive
              ? 'text-[#0891b2] bg-[#0891b2]/10 dark:bg-[#0891b2]/25'
              : 'text-slate-400 hover:text-slate-700 dark:text-slate-500 dark:hover:text-slate-200'
          }`}
          data-testid={`filter-button-${column.key}`}
        >
          <Filter className="w-3 h-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-2" align="start" data-testid={`filter-popover-${column.key}`}>
        <div className="space-y-2">
          <div className="text-xs font-semibold text-slate-700 dark:text-slate-200 px-1">
            Filter: {column.label}
          </div>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search values…"
            className="h-7 text-xs"
            data-testid={`filter-search-${column.key}`}
          />
          <div className="flex justify-between text-xs px-1">
            <button
              type="button"
              onClick={selectAll}
              className="text-[#0891b2] hover:underline"
              data-testid={`filter-all-${column.key}`}
            >
              Select all
            </button>
            <button
              type="button"
              onClick={selectNone}
              className="text-slate-500 hover:underline"
              data-testid={`filter-none-${column.key}`}
            >
              Clear
            </button>
          </div>
          <div className="max-h-56 overflow-y-auto border border-slate-200 dark:border-slate-700 rounded p-1">
            {filtered.length === 0 && (
              <div className="text-xs text-slate-400 px-2 py-1">No matches</div>
            )}
            {filtered.map(([key, label]) => {
              const isChecked = !isActive || (selected || []).includes(key);
              return (
                <label
                  key={String(key)}
                  className="flex items-center gap-2 px-1.5 py-1 hover:bg-slate-50 dark:hover:bg-slate-800 rounded cursor-pointer"
                  data-testid={`filter-option-${column.key}-${String(key)}`}
                >
                  <Checkbox
                    checked={isChecked}
                    onCheckedChange={() => toggleValue(key)}
                    className="h-3.5 w-3.5"
                  />
                  <span className="text-xs text-slate-700 dark:text-slate-200 truncate">
                    {label}
                  </span>
                </label>
              );
            })}
          </div>
          <div className="flex justify-end pt-1">
            <Button
              size="sm"
              variant="ghost"
              className="h-6 text-xs"
              onClick={() => setOpen(false)}
              data-testid={`filter-done-${column.key}`}
            >
              Done
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export function useSortFilterBar(records, columns, storageKey) {
  const [state, setState] = useState(() => loadState(storageKey));
  const [serverDefaultLoaded, setServerDefaultLoaded] = useState(false);
  const [savedMarker, setSavedMarker] = useState(false);

  // Fetch the user's saved server-side default once on mount. When
  // present it wins over any pre-existing localStorage state (the
  // localStorage cache is a session buffer, the server value is the
  // durable per-user default). Runs a single request per storageKey.
  useEffect(() => {
    let cancelled = false;
    const token = (() => {
      try {
        return JSON.parse(localStorage.getItem('user') || 'null')?.token
          || localStorage.getItem('token');
      } catch {
        return localStorage.getItem('token');
      }
    })();
    if (!token) { setServerDefaultLoaded(true); return () => { cancelled = true; }; }
    axios
      .get(`${API}/preferences/sort-filter/${encodeURIComponent(storageKey)}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => {
        if (cancelled) return;
        const pref = r?.data?.preference;
        if (pref) {
          setState({
            sortKey: pref.sortKey ?? null,
            sortDir: pref.sortDir ?? null,
            filters: pref.filters || {},
          });
        }
      })
      .catch(() => { /* 404 = no saved default; keep localStorage state */ })
      .finally(() => { if (!cancelled) setServerDefaultLoaded(true); });
    return () => { cancelled = true; };
  }, [storageKey]);

  // Persist on every change to localStorage (session buffer).
  useEffect(() => {
    saveState(storageKey, state);
  }, [storageKey, state]);

  const setSort = useCallback((key) => {
    setState((prev) => {
      if (prev.sortKey !== key) return { ...prev, sortKey: key, sortDir: 'asc' };
      if (prev.sortDir === 'asc') return { ...prev, sortKey: key, sortDir: 'desc' };
      return { ...prev, sortKey: null, sortDir: null };
    });
  }, []);

  const setFilter = useCallback((key, values) => {
    setState((prev) => ({ ...prev, filters: { ...prev.filters, [key]: values } }));
  }, []);

  const clearFilter = useCallback((key) => {
    setState((prev) => {
      const next = { ...prev.filters };
      delete next[key];
      return { ...prev, filters: next };
    });
  }, []);

  const resetAll = useCallback(() => {
    setState({ sortKey: null, sortDir: null, filters: {} });
  }, []);

  const saveAsDefault = useCallback(async () => {
    const token = (() => {
      try {
        return JSON.parse(localStorage.getItem('user') || 'null')?.token
          || localStorage.getItem('token');
      } catch {
        return localStorage.getItem('token');
      }
    })();
    if (!token) return;
    try {
      await axios.put(
        `${API}/preferences/sort-filter/${encodeURIComponent(storageKey)}`,
        {
          sortKey: state.sortKey,
          sortDir: state.sortDir,
          filters: state.filters || {},
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSavedMarker(true);
      setTimeout(() => setSavedMarker(false), 2000);
    } catch {
      /* swallow — button will not indicate success */
    }
  }, [state, storageKey]);

  // ── Named groupings ────────────────────────────────────────────────
  // Users can save MULTIPLE named sort/filter groupings per report list
  // and switch between them from a dropdown. Named groupings are
  // strictly per-user (server-side, tied to the user id).
  const [groupings, setGroupings] = useState([]);
  const [groupingsOpen, setGroupingsOpen] = useState(false);
  const [groupingsLoaded, setGroupingsLoaded] = useState(false);

  const _getToken = useCallback(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || 'null')?.token
        || localStorage.getItem('token');
    } catch {
      return localStorage.getItem('token');
    }
  }, []);

  const fetchGroupings = useCallback(async () => {
    const token = _getToken();
    if (!token) { setGroupingsLoaded(true); return; }
    try {
      const r = await axios.get(
        `${API}/preferences/sort-filter-groupings/${encodeURIComponent(storageKey)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setGroupings(r?.data?.groupings || []);
    } catch {
      setGroupings([]);
    } finally {
      setGroupingsLoaded(true);
    }
  }, [_getToken, storageKey]);

  useEffect(() => { fetchGroupings(); }, [fetchGroupings]);

  const saveGrouping = useCallback(async () => {
    // Prompt for a name; validate here to avoid a wasted round-trip.
    // eslint-disable-next-line no-alert
    const raw = typeof window !== 'undefined' ? window.prompt('Name this grouping (1-40 chars):') : null;
    if (!raw) return;
    const name = raw.trim();
    if (!name) return;
    if (name.length > 40) {
      // eslint-disable-next-line no-alert
      window.alert('Grouping name must be 40 chars or fewer.');
      return;
    }
    const token = _getToken();
    if (!token) return;
    try {
      await axios.put(
        `${API}/preferences/sort-filter-groupings/${encodeURIComponent(storageKey)}/${encodeURIComponent(name)}`,
        {
          sortKey: state.sortKey,
          sortDir: state.sortDir,
          filters: state.filters || {},
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      await fetchGroupings();
      setGroupingsOpen(true);
    } catch (err) {
      // eslint-disable-next-line no-alert
      window.alert(err?.response?.data?.detail || 'Could not save grouping.');
    }
  }, [state, storageKey, fetchGroupings, _getToken]);

  const applyGrouping = useCallback((g) => {
    setState({
      sortKey: g.sortKey ?? null,
      sortDir: g.sortDir ?? null,
      filters: g.filters || {},
    });
    setGroupingsOpen(false);
  }, []);

  const deleteGrouping = useCallback(async (name) => {
    const token = _getToken();
    if (!token) return;
    try {
      await axios.delete(
        `${API}/preferences/sort-filter-groupings/${encodeURIComponent(storageKey)}/${encodeURIComponent(name)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      await fetchGroupings();
    } catch { /* no-op */ }
  }, [storageKey, fetchGroupings, _getToken]);

  const processedRecords = useMemo(() => {
    let out = Array.isArray(records) ? [...records] : [];

    // Apply filters (each filter is an allow-list).
    Object.entries(state.filters || {}).forEach(([key, allowed]) => {
      if (!Array.isArray(allowed)) return;
      const col = columns.find((c) => c.key === key);
      if (!col) return;
      out = out.filter((r) => {
        const v = col.accessor(r);
        const norm = v === null || v === undefined || v === '' ? '__BLANK__' : v;
        return allowed.includes(norm);
      });
    });

    // Apply sort.
    if (state.sortKey && state.sortDir) {
      const col = columns.find((c) => c.key === state.sortKey);
      if (col) {
        // `sortAccessor` (optional) maps each row to the SORT value — used
        // for enum-style columns (e.g. harvest_correction) where the natural
        // display value isn't sortable alphabetically. When absent we fall
        // back to the regular `accessor`.
        const sortAcc = typeof col.sortAccessor === 'function' ? col.sortAccessor : col.accessor;
        const sortType = col.sortType || col.type || 'text';
        out.sort((a, b) => {
          const cmp = compareValues(sortAcc(a), sortAcc(b), sortType);
          return state.sortDir === 'asc' ? cmp : -cmp;
        });
      }
    }

    return out;
  }, [records, columns, state]);

  const activeFilterCount = Object.values(state.filters || {}).filter((v) => Array.isArray(v))
    .length;
  const hasActive = activeFilterCount > 0 || state.sortKey !== null;

  const bar = (
    <div
      className="flex flex-wrap items-center gap-1.5 px-2 py-2 mb-2 rounded-md bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700"
      data-testid={`sort-filter-bar-${storageKey}`}
    >
      <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400 pl-1 pr-2">
        Sort & filter
      </span>
      {columns.map((col) => {
        const isSorted = state.sortKey === col.key;
        const hasFilter = Array.isArray(state.filters?.[col.key]);
        return (
          <div
            key={col.key}
            className={`flex items-center gap-0.5 rounded-md border px-1.5 py-0.5 text-xs ${
              isSorted || hasFilter
                ? 'border-[#0891b2] bg-white dark:bg-slate-900 shadow-sm'
                : 'border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900'
            }`}
            data-testid={`sort-filter-chip-${col.key}`}
          >
            <button
              type="button"
              onClick={() => setSort(col.key)}
              className="flex items-center gap-1 px-1 py-0.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              data-testid={`sort-button-${col.key}`}
              title={`Sort by ${col.label}`}
            >
              <span className="font-medium text-slate-700 dark:text-slate-200">{col.label}</span>
              {isSorted && state.sortDir === 'asc' && (
                <ArrowUp className="w-3 h-3 text-[#0891b2]" />
              )}
              {isSorted && state.sortDir === 'desc' && (
                <ArrowDown className="w-3 h-3 text-[#0891b2]" />
              )}
              {!isSorted && <ArrowUpDown className="w-3 h-3 text-slate-400" />}
            </button>
            {col.enableFilter !== false && (
              <FilterPopover
                column={col}
                records={records}
                filters={state.filters}
                setFilter={setFilter}
                clearFilter={clearFilter}
              />
            )}
          </div>
        );
      })}
      {hasActive && (
        <button
          type="button"
          onClick={resetAll}
          className="flex items-center gap-1 px-2 py-0.5 text-xs text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded"
          data-testid={`sort-filter-reset-${storageKey}`}
          title="Clear all sort & filter"
        >
          <X className="w-3 h-3" />
          Reset
        </button>
      )}
      <button
        type="button"
        onClick={saveAsDefault}
        className={`flex items-center gap-1 px-2 py-0.5 text-xs rounded transition-colors ${
          savedMarker
            ? 'text-emerald-700 bg-emerald-50 dark:bg-emerald-900/30'
            : 'text-[#0891b2] hover:bg-cyan-50 dark:hover:bg-cyan-900/30'
        }`}
        data-testid={`sort-filter-save-default-${storageKey}`}
        title="Save the current sort & filter as your default for this report"
      >
        {savedMarker ? <Check className="w-3 h-3" /> : <Save className="w-3 h-3" />}
        {savedMarker ? 'Saved as default' : 'Save as default'}
      </button>
      {/* Named groupings — save the current sort+filter under a name and
          switch between saved groupings from a dropdown. */}
      <Popover open={groupingsOpen} onOpenChange={setGroupingsOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex items-center gap-1 px-2 py-0.5 text-xs rounded text-violet-700 hover:bg-violet-50 dark:hover:bg-violet-900/30 transition-colors"
            data-testid={`sort-filter-groupings-${storageKey}`}
            title="Save or switch between named groupings"
          >
            <Layers className="w-3 h-3" />
            Groupings{groupings.length > 0 ? ` (${groupings.length})` : ''}
            <ChevronDown className="w-3 h-3" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-2" align="end">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400 mb-1 px-1">
            Saved groupings
          </div>
          {groupings.length === 0 && (
            <div className="text-xs text-slate-500 dark:text-slate-400 italic px-1 py-2">
              No named groupings yet. Save one below.
            </div>
          )}
          {groupings.length > 0 && (
            <div className="max-h-56 overflow-y-auto flex flex-col gap-0.5">
              {groupings.map((g) => (
                <div
                  key={g.name}
                  className="flex items-center gap-1 px-1 py-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800"
                  data-testid={`grouping-row-${storageKey}-${g.name}`}
                >
                  <button
                    type="button"
                    onClick={() => applyGrouping(g)}
                    className="flex-1 text-left text-xs truncate text-slate-700 dark:text-slate-200 hover:text-[#0891b2]"
                    data-testid={`grouping-apply-${storageKey}-${g.name}`}
                    title={`Load "${g.name}"`}
                  >
                    {g.name}
                  </button>
                  <button
                    type="button"
                    onClick={() => deleteGrouping(g.name)}
                    className="p-1 rounded text-slate-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/30"
                    data-testid={`grouping-delete-${storageKey}-${g.name}`}
                    title={`Delete "${g.name}"`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <button
            type="button"
            onClick={saveGrouping}
            className="mt-2 w-full flex items-center justify-center gap-1 px-2 py-1 text-xs rounded bg-violet-600 text-white hover:bg-violet-700 transition-colors"
            data-testid={`sort-filter-save-grouping-${storageKey}`}
          >
            <Save className="w-3 h-3" />
            Save current as new grouping…
          </button>
        </PopoverContent>
      </Popover>
      <span className="ml-auto text-[10px] text-slate-500 dark:text-slate-400 pr-1">
        Showing {processedRecords.length} / {records.length}
      </span>
    </div>
  );

  return { processedRecords, bar, resetAll, hasActive };
}

export default useSortFilterBar;
